import {useCallback} from 'react';

import {assert} from 'Asserts';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import checkSoftInvariant from 'lib/checkSoftInvariant';
import DocumentStore from 'notebook/documents/DocumentStore';
import {resolveSelectorsFromPastedHTMLTables} from 'notebook/plain-tables/selectors/SelectorConflictResolver';
import {getTableLengthErrorMessage} from 'notebook/structured-tables/util/checkTableCapacity';
import {type InitialCellValue} from 'notebook/structured-tables/util/getInitialCellValueFromLiveValue';
import type {Cell, PastedCell} from 'notebook/tables/constants/CellConstants';
import {type InsertableColumnInfo} from 'notebook/tables/constants/TableColumnConstants';
import * as TableConstants from 'notebook/tables/constants/TableConstants';
import {MultiSelectionBox, TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {type TableOperation} from 'notebook/tables/excess/ops/TableOpConstants';
import {ReorderOptions} from 'notebook/tables/excess/ops/TableOps';
import {useTableOps} from 'notebook/tables/spreadsheets/TableOpContext';
import {TableModelContext} from 'notebook/tables/store/contexts/TableModelContext';
import {useTableModel} from 'notebook/tables/store/hooks/useTableModel';
import {clearCellsInRange as clearCellsInRangeMutation} from 'notebook/tables/store/logic/mutations/clearCellsInRange';
import {
  pasteTable as pasteTableMutation,
  preparePastedCells,
  processPastedTypedValues,
} from 'notebook/tables/store/logic/mutations/pasteTable';
import {
  toggleHeaderCol as toggleHeaderColMutation,
  toggleHeaderRow as toggleHeaderRowMutation,
} from 'notebook/tables/store/logic/mutations/toggleHeader';
import {updateCell as updateCellMutation} from 'notebook/tables/store/logic/mutations/updateCell';
import {computeAnyCellsLockedOrArchived} from 'notebook/tables/store/logic/TableLockingLogic';
import {computeMergeValidation, type MergeValidation} from 'notebook/tables/store/logic/TableMergingLogic';
import {computePasteRejectionReason} from 'notebook/tables/store/logic/TablePasteLogic';
import {computeExpandedSelectionBox} from 'notebook/tables/store/logic/TableSelectionLogic';
import TableUtil from 'notebook/tables/TableUtil';
import {
  trackCrossStepReferenceChange,
  trackCrossStepReferenceBulkDelete,
} from 'notebook/tables/util/analytics/CrossStepReferenceAnalytics';
import {getWorksheetIdFromNamespace} from 'notebook/tables/util/WorksheetTableNamespaceUtil';
import notifications from 'notifications';
import {trackContainerValidityForTableEvents} from 'samples/containers/ValidContentsUtils';

interface TableOpsMethods {
  renameTable: (tableName: string, newTableName: string) => void;
  performTableOp?: (tableOp: TableOperation) => void;
  deleteColumns: (tableName: string, start: number, end: number) => void;
  insertColumnsLeft: (
    tableName: string,
    start: number,
    end: number,
    columnInfo: InsertableColumnInfo | {},
    initialCellValues?: Array<InitialCellValue>
  ) => void;
  insertColumnsRight: (
    tableName: string,
    start: number,
    end: number,
    columnInfo: InsertableColumnInfo | {},
    initialCellValues?: Array<InitialCellValue>
  ) => void;
  deleteRows: (tableName: string, start: number, end: number) => void;
  deleteRowsNonContiguous: (tableName: string, ranges: Array<[number, number]>) => void;
  insertRowsAbove: (tableName: string, start: number, end: number) => void;
  insertRowsBelow: (tableName: string, start: number, end: number) => void;
  mergeRegion: ((tableName: string, selectedBox: MultiSelectionBox) => void) | null;
  unmergeRegion: ((tableName: string, selectedBox: MultiSelectionBox) => void) | null;
  reorderRowsOrColumns: (tableName: string, options: ReorderOptions) => void;
}

export interface TableMutationMethods extends TableOpsMethods {
  /**
   * Update a single cell in the table.
   *
   * @param cellIndex - The [row, column] index of the cell to update
   * @param cellData - Partial cell data to merge into the existing cell
   * @param expandTable - If true, add a new blank row at the bottom
   */
  updateCell: (cellIndex: TableIndex, cellData: Partial<Cell>, expandTable?: boolean) => void;

  /**
   * Clear all cells within a selection box.
   *
   * @param selectionBox - The box defining which cells to clear
   */
  clearCellsInRange: (selectionBox: MultiSelectionBox) => void;

  /**
   * Clear all cells within the current selection, expanding to fully contain any
   * intersecting merged regions. No-op when there is no active selection.
   */
  clearSelection: () => void;

  /**
   * Toggle the header column flag on the table, clearing bold formatting on the first column.
   */
  toggleHeaderCol: () => void;

  /**
   * Toggle the header row flag on the table, clearing bold formatting on the first row.
   */
  toggleHeaderRow: () => void;

  /**
   * Validate whether the current selection can be merged into a single cell.
   *
   * Reads the merge-expanded selection box from the model and reports whether the
   * selection straddles the freeze boundary (merging disallowed) and whether the
   * merge would discard cell values (caller should confirm). Returns `null` when
   * there is no active selection.
   *
   * The merge itself is performed separately via `mergeRegion`; the confirmation
   * modal is owned by the component.
   */
  validateMerge: () => MergeValidationResult | null;

  /**
   * Paste a 2D array of cells into the table.
   *
   * Orchestrates the full paste pipeline (excluding clipboard parsing and file
   * attachment, which remain in the component): resolves the destination
   * selection, tiles/reshapes the pasted cells to fill it, validates the paste
   * (surfacing warnings/errors via notifications), normalizes typed values,
   * asynchronously resolves selectors, writes the cells via the pure
   * `pasteTable` mutation, enforces capacity limits, remerges single-input
   * columns, persists the change, and tracks analytics.
   *
   * @param cells - The 2D array of cells to paste (already parsed from the clipboard).
   * @param options - Paste options: `transpose` and the originating `selectionIndex`.
   * @param setSelection - Component callback to update the multi-selection. Called
   *   once with the expanded paste region (so a rejected paste is visible) and again
   *   with the final pasted region.
   */
  pasteTable: (
    cells: Array<Array<PastedCell>>,
    options: PasteTableOptions,
    setSelection: (args: {start: TableIndex; end: TableIndex; text?: string}) => void
  ) => void;
}

/** Options for {@link TableMutationMethods.pasteTable}. */
export interface PasteTableOptions {
  /** Whether to transpose the pasted data before pasting. */
  transpose?: boolean;
  /**
   * The cell index that originated the paste. Used for async pastes (e.g. context
   * menu) that may resolve after the active selection has changed.
   */
  selectionIndex?: TableIndex | null;
}

/**
 * The result of validating a merge of the current selection, including the
 * merge-expanded box that should be passed to `mergeRegion`.
 */
export interface MergeValidationResult extends MergeValidation {
  /** The merge-expanded selection box to merge. */
  box: MultiSelectionBox;
}

/**
 * Hook that provides table mutation methods with analytics and persistence.
 *
 * All data is fetched fresh from the TableModel at execution time,
 * ensuring we always use the latest state and keeping callbacks stable.
 */
export function useTableMutations(): TableMutationMethods {
  const {useTableEditableStore, tableModelSelector} = TableModelContext.useContext();
  const tableOps = useTableOps();
  const {tableModel: disableMergingCells} = useTableModel((state) => state.props.disableMergingCells);

  const getTableModel = useCallback(
    () => tableModelSelector(useTableEditableStore.getState()),
    [useTableEditableStore, tableModelSelector]
  );

  const updateCell = useCallback(
    (cellIndex: TableIndex, cellData: Partial<Cell>, expandTable?: boolean): void => {
      const model = getTableModel();
      const {table, index, fileId, tableNamespace, updateHandler} = model.props;
      const {boundaryIndex: singleInputBoundaryIndex} = model.singleInputs;

      // Execute pure mutation
      const result = updateCellMutation(table, cellIndex, cellData, {
        expandTable,
        singleInputBoundaryIndex,
      });

      // Track cross-step reference analytics
      if (cellData.text != null) {
        const oldCellText = result.affectedCells[0]?.oldCell?.text ?? '';
        const worksheetId = getWorksheetIdFromNamespace(tableNamespace);
        const worksheetInfo = worksheetId
          ? {worksheetId, stepCount: DocumentStore.getRelatedDocumentIds(fileId).length}
          : null;
        trackCrossStepReferenceChange(oldCellText, cellData.text, worksheetInfo);
      }

      // Persist the change
      updateHandler(
        index,
        {table: result.table},
        undefined,
        result.tableOp != null ? {[table.name]: result.tableOp} : undefined
      );

      // Track container validity analytics
      const newCell = result.affectedCells[0]?.newCell;
      if (newCell) {
        trackContainerValidityForTableEvents(fileId, table.apiId!, [
          {event: ELN_ANALYTICS_ACTIONS.ADDED_CONTAINERS_TO_STRUCTURED_TABLE, cells: [newCell]},
        ]);
      }
    },
    [getTableModel]
  );

  const clearCellsInRange = useCallback(
    (selectionBox: MultiSelectionBox): void => {
      const model = getTableModel();
      const {table, index, fileId, tableNamespace, updateHandler} = model.props;

      // Execute pure mutation
      const result = clearCellsInRangeMutation(table, selectionBox);

      // Track cross-step reference analytics
      const oldCells = result.affectedCells.map((c) => c.oldCell).filter((c): c is Cell => c != null);
      const worksheetId = getWorksheetIdFromNamespace(tableNamespace);
      const worksheetInfo = worksheetId
        ? {worksheetId, stepCount: DocumentStore.getRelatedDocumentIds(fileId).length}
        : null;
      trackCrossStepReferenceBulkDelete(oldCells, worksheetInfo);

      // Persist the change
      updateHandler(
        index,
        {table: result.table},
        undefined,
        result.tableOp != null ? {[table.name]: result.tableOp} : undefined
      );

      // Track container validity analytics
      if (oldCells.length > 0) {
        trackContainerValidityForTableEvents(fileId, table.apiId!, [
          {
            event: ELN_ANALYTICS_ACTIONS.REMOVED_CONTAINERS_FROM_STRUCTURED_TABLE,
            cells: oldCells,
          },
        ]);
      }
    },
    [getTableModel]
  );

  const clearSelection = useCallback((): void => {
    const model = getTableModel();
    const {expandedBox} = model.selection;

    // Nothing to clear when there is no active selection.
    if (expandedBox == null) {
      return;
    }

    clearCellsInRange(expandedBox);
  }, [getTableModel, clearCellsInRange]);

  const toggleHeaderCol = useCallback((): void => {
    const model = getTableModel();
    const {table, index, updateHandler} = model.props;

    const result = toggleHeaderColMutation(table);

    updateHandler(
      index,
      {table: result.table},
      undefined,
      result.tableOp != null ? {[table.name]: result.tableOp} : undefined
    );
  }, [getTableModel]);

  const toggleHeaderRow = useCallback((): void => {
    const model = getTableModel();
    const {table, index, updateHandler} = model.props;

    const result = toggleHeaderRowMutation(table);

    updateHandler(
      index,
      {table: result.table},
      undefined,
      result.tableOp != null ? {[table.name]: result.tableOp} : undefined
    );
  }, [getTableModel]);

  const validateMerge = useCallback((): MergeValidationResult | null => {
    const model = getTableModel();
    const {expandedBox} = model.selection;

    // Nothing to validate when there is no active selection.
    if (expandedBox == null) {
      return null;
    }

    const {table} = model.props;
    const freezeBoundary = TableUtil.getFreezeBoundary(table);
    const validation = computeMergeValidation(table.table, expandedBox, freezeBoundary);

    return {box: expandedBox, ...validation};
  }, [getTableModel]);

  const pasteTable = useCallback(
    (
      cells: Array<Array<PastedCell>>,
      options: PasteTableOptions,
      setSelection: (args: {start: TableIndex; end: TableIndex; text?: string}) => void
    ): void => {
      const model = getTableModel();
      const {
        table,
        computedTable,
        preserveMergedCellsInPaste,
        allowMergeSelection,
        selectorsLink,
        selectorsEnabled,
      } = model.props;
      const {multiStartIndex, multiEndIndex} = model.state;
      const {selectionIndex} = options;

      // Async paste (e.g. context menu) can resolve after the user clicks elsewhere,
      // so the current multi-selection may no longer match the cell that initiated the
      // paste. options.selectionIndex captures that originating cell. If it still falls
      // inside the active multi-selection, keep the full range so tile-fill works;
      // otherwise paste into just the originating cell.
      let selectionStart: TableIndex;
      let selectionEnd: TableIndex;
      if (
        selectionIndex != null &&
        multiStartIndex != null &&
        multiEndIndex != null &&
        TableUtil.isIndexBetween(multiStartIndex, multiEndIndex, selectionIndex)
      ) {
        selectionStart = multiStartIndex;
        selectionEnd = multiEndIndex;
      } else if (selectionIndex != null) {
        selectionStart = selectionIndex;
        selectionEnd = selectionIndex;
      } else if (multiStartIndex != null && multiEndIndex != null) {
        selectionStart = multiStartIndex;
        selectionEnd = multiEndIndex;
      } else {
        checkSoftInvariant(false, {
          message: 'Unable to paste: no selection or originating cell index available',
          expirationDate: '2027-04-01',
          errorInDevAndTest: true,
        });
        return;
      }

      const box = computeExpandedSelectionBox(selectionStart, selectionEnd, table, computedTable);
      // Matches the original `TableEditable._multiSelectionBox`, which hard-asserts a
      // non-null box in all environments (including production).
      assert(box != null, 'Expected to have a selection');

      // 1. Tile / reshape the pasted cells so they fill the selection.
      const preparedCells = preparePastedCells(table, computedTable, cells, box, {
        transpose: options.transpose,
        preserveMergedCellsInPaste: Boolean(preserveMergedCellsInPaste),
        disallowCustomColumns: Boolean(table.disallowCustomColumns),
      });

      // Set the selection according to the expanded paste table so that if it fails the user can see the issue
      setSelection({
        start: [box.minI, box.minJ],
        end: [
          Math.max(box.maxI, box.minI + preparedCells.length - 1),
          Math.max(box.maxJ, box.minJ + preparedCells[0].length - 1),
        ],
      });

      // 2. Validate the paste before mutating.
      const pasteRejection = computePasteRejectionReason(
        table,
        computedTable,
        preparedCells,
        box.minI,
        box.minJ,
        {
          supportsMerge: model.merging.supportsMerge,
          preserveMergedCellsInPaste: Boolean(preserveMergedCellsInPaste),
          allowMergeSelection: allowMergeSelection ?? undefined,
          isAnyCellLockedOrArchived: (lockCells) => computeAnyCellsLockedOrArchived(lockCells, table.rowData),
        }
      );
      if (pasteRejection != null) {
        if (pasteRejection.severity === 'warn') {
          notifications.warn(pasteRejection.reason);
        } else {
          notifications.error(pasteRejection.reason);
        }
        return;
      }

      // 3. Normalize typed values.
      const processedCells = processPastedTypedValues(table, preparedCells);

      // 4. Resolve selectors, then paste the tiled table at the top-left cell of the selection.
      resolveSelectorsFromPastedHTMLTables({
        pastedTables: [processedCells],
        existingSelectors: selectorsLink.value,
        // `selectorsEnabled` is optional and defaults to enabled, matching the
        // default applied to the `TableEditable` component props.
        selectorsEnabled: selectorsEnabled ?? true,
      }).then((selectors) => {
        // Selector resolution is async, so the table may have changed since the paste
        // was initiated. Re-read the latest model here so we paste against the current
        // table / computedTable / handlers, matching the original `_pasteTable` which
        // read `this.props.*` live inside the resolution callback. The prepared cells
        // and top-left paste coordinate (box.minI / box.minJ) are intentionally kept
        // from the initiation-time snapshot, exactly as the original did.
        const latestModel = getTableModel();
        const {
          table: latestTable,
          index: latestIndex,
          fileId: latestFileId,
          computedTable: latestComputedTable,
          preserveMergedCellsInPaste: latestPreserveMergedCellsInPaste,
          updateHandler: latestUpdateHandler,
        } = latestModel.props;

        const pasteResult = pasteTableMutation(
          latestTable,
          latestComputedTable,
          processedCells,
          box.minI,
          box.minJ,
          Boolean(latestPreserveMergedCellsInPaste)
        );
        const {box: pasteBox, tableOp} = pasteResult;
        let {table: newTable} = pasteResult;

        const tableMetadata = latestTable.metadata
          ? (latestTable.metadata as TableConstants.StructuredTableToTableMetadata<unknown>)
          : undefined;

        // If tableMetadata is null, do not check for capacity here - it may be a plain table. Structured tables
        // will still catch the capacity issue before making changes, via a check in StructuredTablePlugin.setContent.
        const capacityErrorMsg =
          tableMetadata != null
            ? getTableLengthErrorMessage({
                currentTableLength: latestTable.table.length,
                newTableLength: newTable.table.length,
                structuredTableKind: tableMetadata.kind,
                isTableEditable: true,
                allowAddRows: latestModel.dimensions.canAddRows,
                allowRemoveRows: latestModel.dimensions.canRemoveRows,
              })
            : null;
        if (capacityErrorMsg) {
          notifications.error(capacityErrorMsg);
          return;
        }

        // TODO(T7414): Refactor into `excess/ops/TableOps`
        if (TableUtil.isInvalidOpWithWarn(latestTable, newTable)) {
          return;
        }

        // If any single inputs are present and the table is expanding, merge the single input columns accordingly.
        if (latestModel.singleInputs.boundaryIndex && newTable.table.length > latestTable.table.length) {
          // TODO(BNCH-136509): Possibly update tableOp to include single input merged cells?
          newTable = TableUtil.remergeSingleInputColumns(newTable);
        }

        // Update state (table and selectors) / selection
        const replacedCells = latestTable.table
          .slice(pasteBox.minI, pasteBox.maxI + 1)
          .flatMap((row) => row.slice(pasteBox.minJ, pasteBox.maxJ + 1));
        setSelection({
          start: [pasteBox.minI, pasteBox.minJ],
          end: [pasteBox.maxI, pasteBox.maxJ],
          text: newTable.table[pasteBox.minI][pasteBox.minJ].text,
        });
        latestUpdateHandler(latestIndex, {table: newTable}, selectors, {[newTable.name]: tableOp});

        // Track container validity analytics
        trackContainerValidityForTableEvents(latestFileId, latestTable.apiId!, [
          {
            event: ELN_ANALYTICS_ACTIONS.ADDED_CONTAINERS_TO_STRUCTURED_TABLE,
            cells: processedCells.flat(),
          },
          {event: ELN_ANALYTICS_ACTIONS.REMOVED_CONTAINERS_FROM_STRUCTURED_TABLE, cells: replacedCells},
        ]);
      });
    },
    [getTableModel]
  );

  return {
    updateCell,
    clearCellsInRange,
    clearSelection,
    toggleHeaderCol,
    toggleHeaderRow,
    validateMerge,
    pasteTable,
    renameTable: tableOps.renameTable,
    performTableOp: tableOps.performTableOp,
    deleteColumns: tableOps.deleteTableColumns,
    insertColumnsLeft: tableOps.insertTableColumnsLeft,
    insertColumnsRight: tableOps.insertTableColumnsRight,
    deleteRows: tableOps.deleteTableRows,
    deleteRowsNonContiguous: tableOps.deleteTableRowsNonContiguous,
    insertRowsAbove: tableOps.insertTableRowsAbove,
    insertRowsBelow: tableOps.insertTableRowsBelow,
    mergeRegion: disableMergingCells ? null : tableOps.mergeTableRegion,
    unmergeRegion: disableMergingCells ? null : tableOps.unmergeTableRegion,
    reorderRowsOrColumns: tableOps.reorderTableRowsOrColumns,
  };
}
